import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import {
  useFloating,
  offset,
  shift,
  flip,
  useHover,
  useFocus,
  useInteractions,
  FloatingPortal,
  Placement as FloatingPlacement,
  safePolygon,
} from "@floating-ui/react";
import styled from "@emotion/styled";
import { MDSHoverContentPlacement, MDSHoverContentProps } from "./hover-content.types";
// This is a pure utility hook that only have React dependencies
// eslint-disable-next-line no-restricted-imports
import useDebouncedCallback from "@/domains/react/useDebouncedCallback";
// This will be moved into @mds
// eslint-disable-next-line no-restricted-imports
import { ZIndex } from "@/domains/design/constants";

const getPlacement = (placement?: MDSHoverContentPlacement): FloatingPlacement => {
  switch (placement) {
    case MDSHoverContentPlacement.Above:
      return "top-start";
    case MDSHoverContentPlacement.Below:
      return "bottom-start";
    case MDSHoverContentPlacement.BelowRightAlignment:
      return "bottom-end";
    case MDSHoverContentPlacement.Right:
      return "right-start";
  }
  return "bottom-start";
};

/**
 * @deprecated
 * This component is deprecated.
 * Please prefer to use the components in @/mds/components/menu instead
 */
export const MDSHoverContent = memo<MDSHoverContentProps>(function MDSHoverContent({
  children,
  isTouchDevice,
  content,
  group,
  requireClick,
  placement,
}) {
  const [show, setShow] = useState(false);

  const { x, y, strategy, refs, context } = useFloating({
    open: show,
    onOpenChange: setShow,
    placement: getPlacement(placement),
    middleware: [offset(10), flip(), shift()],
  });

  const hover = useHover(context, {
    enabled: !requireClick,
    handleClose: safePolygon({ requireIntent: true }),
  });
  const focus = useFocus(context);
  const { getReferenceProps, getFloatingProps } = useInteractions([hover, focus]);

  const hideWithDelay = useDebouncedCallback(
    (e?: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      e?.stopPropagation();
      setShow(false);
    },
    250,
    { leading: false, trailing: true }
  );

  const hideImmediately = useCallback(() => {
    hideWithDelay();
    hideWithDelay.flush();
  }, [hideWithDelay]);

  const activateHoveringMode = useCallback(() => {
    hideWithDelay.cancel();
    group?.hideAll({ exceptHideFn: hideWithDelay });
    setShow(true);
  }, [hideWithDelay, group]);

  const toggleVisibility = useCallback(() => {
    if (show) hideImmediately();
    else activateHoveringMode();
  }, [show, hideImmediately, activateHoveringMode]);

  useEffect(() => {
    if (!group) return;

    group.hideFns.push(hideWithDelay);

    return () => {
      group.hideFns = group.hideFns.filter(hide => hide !== hideWithDelay);
    };
  }, [hideWithDelay, group]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!event.target) return;
      // If the target is in the floating element, do nothing
      if (refs.floating.current?.contains(event.target as Node)) return;
      // If the target is in the floating element, do nothing
      if (refs.domReference.current?.contains(event.target as Node)) return;
      hideImmediately();
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [hideImmediately, refs.domReference, refs.floating]);

  const renderedContent = useMemo(
    () =>
      show ? (
        <FloatingPortal>
          <StyledContainer
            ref={refs.setFloating}
            style={{
              position: strategy,
              top: y ?? 0,
              left: x ?? 0,
            }}
            {...getFloatingProps()}
          >
            {content({ hide: hideImmediately })}
          </StyledContainer>
        </FloatingPortal>
      ) : null,
    [content, hideImmediately, show, strategy, x, y, getFloatingProps, refs.setFloating]
  );

  if (isTouchDevice) {
    return <>{children}</>;
  }

  return (
    <StyledToggleWrapper
      ref={refs.setReference}
      onClick={requireClick ? toggleVisibility : undefined}
      {...getReferenceProps()}
    >
      {children}
      {renderedContent}
    </StyledToggleWrapper>
  );
});

const StyledToggleWrapper = styled.div`
  position: relative;
`;

const StyledContainer = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  z-index: ${ZIndex.HoverCard};
`;
